import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  updatePresentation,
  viewPresentation,
} from "../../redux/features/presentations/presentationsSlice";
import {
  selectHasPermission,
  selectUserRole,
} from "../../redux/features/auth/authSlice";
import Footer from "../Footer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRotateLeft,
  faFloppyDisk,
  faPlus,
  faSpinner,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import Navbar from "../Navbar";
import { toast } from "react-toastify";
import AnimationEditor from "../AnimateTab/AnimationEditor";
import { appendNestedFormData } from "../../utils/appendNestedFormData";
import { resolveAnimationDataForUpload } from "../../utils/animationDataUtils";
import EditLeftPanel from "./EditLeftPanel";
import EditMiddlePanel from "./EditMiddlePanel";
import EditRightPanel from "./EditRightPanel";

function EditPresentation() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { uuid } = useParams();
  const { updatePresentationLoading, selectedPresentation } = useSelector(
    (state) => state.presentation,
  );
  const userRole = useSelector(selectUserRole);
  const isAdmin =
    userRole &&
    ["admin", "super admin", "super"].includes(userRole.toLowerCase());
  const canUpdatePresentations = useSelector((state) =>
    selectHasPermission(state, "update presentations"),
  );
  const canReorderSlides = useSelector((state) =>
    selectHasPermission(state, "reorder slides"),
  );
  const isReorderOnly = !isAdmin && canReorderSlides;

  const [slides, setSlides] = useState([]);
  const [activeSlide, setActiveSlide] = useState(null);
  const [activeTab, setActiveTab] = useState("interactive");
  const [activeQuestionTab, setActiveQuestionTab] = useState("poll");
  const [presentationTitle, setPresentationTitle] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [deletedSlides, setDeletedSlides] = useState([]);
  const [isAnimateMode, setIsAnimateMode] = useState(false);
  const [animatedSlides, setAnimatedSlides] = useState({});
  const [slidesAnimationData, setSlidesAnimationData] = useState({});
  const [pendingAnimationAssets, setPendingAnimationAssets] = useState({});
  const [presentationAssets, setPresentationAssets] = useState([]);

  const isCurrentSlideAnimated =
    activeSlide !== null && !!animatedSlides[activeSlide];

  const activeSlideImageUrl =
    slides[activeSlide]?.type === "image"
      ? slides[activeSlide]?.image?.image_url || null
      : null;

  const handleSaveAnimation = (data, pendingFiles = {}) => {
    setSlidesAnimationData((prev) => ({ ...prev, [activeSlide]: data }));
    setAnimatedSlides((prev) => ({ ...prev, [activeSlide]: true }));
    if (pendingFiles && Object.keys(pendingFiles).length > 0) {
      setPendingAnimationAssets((prev) => ({ ...prev, ...pendingFiles }));
    }
  };

  useEffect(() => {
    if (uuid) {
      dispatch(viewPresentation(uuid));
    }
  }, [uuid, dispatch]);

  useEffect(() => {
    if (isReorderOnly) {
      setActiveTab("content");
    }
  }, [isReorderOnly]);

  useEffect(() => {
    if (selectedPresentation && selectedPresentation.slides.length > 0) {
      setPresentationTitle(selectedPresentation.title || "");

      const formattedSlides = selectedPresentation.slides.map((slide, idx) => {
        const slideType = slide.type || slide.type; // Handle snake_case from backend

        if (slideType === "blank") {
          return {
            id: slide.id,
            title: slide.title,
            is_active: slide.is_active !== false,
            type: "blank",
          };
        } else if (
          slideType === "question" ||
          (slide.question && !slide.image)
        ) {
          return {
            id: slide.id,
            title: slide.title,
            is_active: slide.is_active !== false,
            type: "question",
            question: {
              id: slide.question.id,
              question_text: slide.question.question_text,
              question_type: slide.question.question_type,
              choices:
                slide.question.choices?.map((c) => ({
                  id: c.id,
                  choice_text: typeof c === "object" ? c.choice_text : c,
                  order: c.order,
                })) || [],
            },
          };
        } else if (slideType === "image" || slide.image) {
          let url = slide.image?.image_url;
          const imageId = slide.image_id || slide.image?.id;

          if (url && !url.startsWith("http") && !url.startsWith("blob:")) {
            try {
              const apiUrl =
                process.env.REACT_APP_API_URL || "http://localhost:49152";
              const origin = new URL(apiUrl).origin;
              url = `${origin}${url.startsWith("/") ? "" : "/"}${url}`;
            } catch (e) {
              console.error("Error parsing API URL:", e);
              url = `${process.env.REACT_APP_API_URL || "http://localhost:49152"}${url}`;
            }
          }

          return {
            id: slide.id,
            title: slide.title,
            is_active: slide.is_active !== false,
            type: "image",
            image: {
              id: imageId,
              image_url: url, // backend stored image normalized
              image_title: slide.title,
            },
          };
        }
        return slide;
      });
      setSlides(formattedSlides);
      setActiveSlide(0);

      // Restore animation state from backend
      const restoredAnimData = {};
      const restoredAnimatedFlags = {};
      selectedPresentation.slides.forEach((slide, idx) => {
        if (slide.with_animation) {
          restoredAnimatedFlags[idx] = true;
        }
        if (slide.animation_data) {
          try {
            const parsed =
              typeof slide.animation_data === "string"
                ? JSON.parse(slide.animation_data)
                : slide.animation_data;
            restoredAnimData[idx] = parsed;
            restoredAnimatedFlags[idx] = true;
          } catch (_) {}
        }
      });
      setSlidesAnimationData(restoredAnimData);
      setAnimatedSlides(restoredAnimatedFlags);

      // Normalize asset src URLs from relative paths to absolute
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:49152";
      let origin = apiUrl;
      try {
        origin = new URL(apiUrl).origin;
      } catch (_) {}
      const normalizedAssets = (selectedPresentation.assets || []).map((a) => ({
        id: String(a.id),
        name: a.name || "Asset",
        type: a.type || "image",
        thumbnail: a.src
          ? a.src.startsWith("http")
            ? a.src
            : `${origin}${a.src.startsWith("/") ? "" : "/"}${a.src}`
          : null,
      }));
      setPresentationAssets(normalizedAssets);
    }
  }, [selectedPresentation]);

  useEffect(() => {
    if (activeSlide === null || !slides[activeSlide]) return;
    const rawSlide = selectedPresentation?.slides?.[activeSlide];
  }, [activeSlide, slides]);

  const handleTitleChange = (e) => {
    setPresentationTitle(e.target.value);
  };
  const getLinks = () => {
    if (!uuid) return { speaker: "", attendee: "" };
    return {
      speaker: `${process.env.REACT_APP_URL}/${uuid}`,
      attendee: `${process.env.REACT_APP_URL}/${uuid}/attendee`,
    };
  };

  const handleAddSlide = (data) => {
    const newSlide = {
      id: Date.now(), // Generate temporary ID for new slides
      isNew: true,
      is_active: true,
      presentationId: uuid,
      type: "question",
      title:
        data.type === "poll"
          ? "Poll Question:"
          : data.type === "multiple"
            ? "Multiple Question:"
            : "Text Question:",
      question: {
        question_type: data.type,
        id: data.id || null, // optional
        question_text: data.question || "",
        choices: (data.choices || []).map((c, index) => ({
          choice_text: typeof c === "string" ? c : c.choice_text,
          order: index + 1,
        })),
      },
    };

    setSlides((prev) => {
      const updated = [...prev, newSlide];
      return updated;
    });
    setActiveSlide(slides.length);
  };

  const handleUpdatePresentation = async () => {
    try {
      const formData = new FormData();
      const { speaker } = getLinks();

      // Presentation fields
      formData.append("presentation[title]", presentationTitle);
      formData.append("presentation[presentation_url]", speaker);

      // Slides
      slides.forEach((slide, i) => {
        const type = slide.type?.toLowerCase();
        if (slide.id && !slide.isNew) {
          formData.append(`slides[${i}][id]`, slide.id);
        }

        formData.append(`slides[${i}][is_active]`, slide.is_active ? 1 : 0);
        formData.append(`slides[${i}][order]`, i + 1);
        formData.append(`slides[${i}][type]`, slide.type);
        formData.append(`slides[${i}][title]`, slide.title || "");

        // ✅ Handle Question Slides properly
        if (type === "question") {
          const q = slide.question || {};
          if (q.id) {
            formData.append(`slides[${i}][question][id]`, q.id);
          }
          formData.append(
            `slides[${i}][question][question_type]`,
            q.question_type || "",
          );
          formData.append(
            `slides[${i}][question][question_text]`,
            q.question_text || "",
          );

          if (
            (q.question_type === "poll" || q.question_type === "multiple") &&
            Array.isArray(q.choices)
          ) {
            q.choices.forEach((choice, index) => {
              if (choice.id) {
                formData.append(
                  `slides[${i}][question][choices][${index}][id]`,
                  choice.id,
                );
              }
              formData.append(
                `slides[${i}][question][choices][${index}][choice_text]`,
                choice.choice_text || "",
              );
              formData.append(
                `slides[${i}][question][choices][${index}][order]`,
                choice.order || index + 1,
              );
            });
          }
        }

        if (type === "image") {
          const imageId = slide.image?.id;
          if (imageId) {
            formData.append(`slides[${i}][image_id]`, imageId);
            formData.append(
              `slides[${i}][image_url]`,
              slide.image.image_url || "",
            );
          } else if (slide.image_file instanceof File) {
            formData.append(`slides[${i}][image_title]`, slide.title || "");
            formData.append(`slides[${i}][image]`, slide.image_file);
          }
        }

        if (animatedSlides[i] && slidesAnimationData[i]) {
          formData.append(`slides[${i}][with_animation]`, 1);
          appendNestedFormData(
            formData,
            `slides[${i}][animation_data]`,
            resolveAnimationDataForUpload(slidesAnimationData[i]),
          );
        }
      });
      // Append deleted slides
      deletedSlides.forEach((id, index) => {
        formData.append(`deleted_slides[${index}]`, id);
      });

      // New animation asset files (images dropped in the animation editor, keyed by temp placeholder)
      Object.entries(pendingAnimationAssets).forEach(([tempKey, file]) => {
        formData.append(`assets[${tempKey}]`, file);
      });

      await dispatch(updatePresentation({ uuid, data: formData })).unwrap();
      toast.success("Presentation updated successfully!");
      navigate("/presentations");
      setDeletedSlides([]);
    } catch (error) {
      toast.error(error.message || "Failed to update presentation.");
    }
  };

  const currentSlideBackground =
    slidesAnimationData?.[activeSlide]?.canvasBackground;

  const currentSlideBackgroundColor =
    slidesAnimationData?.[activeSlide]?.canvasBackgroundColor || "#f3f4f6";

  return (
    <div className="min-h-screen bg-lightPurple/5">
      <Navbar />
      <div className="flex justify-between items-center px-8 mt-6">
        <Link to="/presentations">
          <button className="p-2 text-sm rounded-lg bg-purple-400 hover:bg-purple-500 text-white font-bold w-24 uppercase">
            <FontAwesomeIcon icon={faArrowRotateLeft} className="mr-1" />
            Back
          </button>
        </Link>
      </div>
      <div className="text-center mt-4">
        <h2 className="font-semibold text-3xl mb-6">
          {isReorderOnly ? "Reorder Presentation" : "Update Presentation"}
        </h2>
        <div>
          <label className="text-2xl font-medium">Presentation Title :</label>
          <input
            type="text"
            value={presentationTitle}
            onChange={handleTitleChange}
            readOnly={isReorderOnly}
            placeholder="Enter presentation title"
            className={`ml-2 rounded-lg border border-gray-300 p-2 w-[30%] focus:outline-none focus:ring-2 focus:ring-lightPurple capitalize ${isReorderOnly ? "bg-gray-100 cursor-not-allowed" : ""}`}
          />
        </div>
      </div>

      {isAnimateMode ? (
        <div className="px-8 pb-12">
          <div className="mb-4">
            <button
              onClick={() => setIsAnimateMode(false)}
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-purpleColor transition font-medium"
            >
              ← Back to Slide Editor
            </button>
          </div>
          <AnimationEditor
            slideImageUrl={activeSlideImageUrl}
            onSave={handleSaveAnimation}
            initialData={slidesAnimationData[activeSlide] || null}
            initialAssets={presentationAssets}
            slideContent={
              slides[activeSlide]?.type === "question"
                ? {
                    question: slides[activeSlide].question?.question_text || "",
                    questionType:
                      slides[activeSlide].question?.question_type || "poll",
                    choices: (slides[activeSlide].question?.choices || []).map(
                      (c) => (typeof c === "object" ? c.choice_text : c),
                    ),
                  }
                : null
            }
          />
        </div>
      ) : (
        <div className="grid grid-cols-12 gap-4 p-8 ">
          {/* Left Panel: Slides List */}
          <EditLeftPanel
            slides={slides}
            activeSlide={activeSlide}
            setActiveSlide={setActiveSlide}
            isReorderOnly={isReorderOnly}
            isDragging={isDragging}
            isProcessing={isProcessing}
            slidesAnimationData={slidesAnimationData}
            uuid={uuid}
            setSlides={setSlides}
            setIsProcessing={setIsProcessing}
            setIsDragging={setIsDragging}
            draggedIndex={draggedIndex}
            setDraggedIndex={setDraggedIndex}
            setDeletedSlides={setDeletedSlides}
            setSlidesAnimationData={setSlidesAnimationData}
            setAnimatedSlides={setAnimatedSlides}
          />

          {/* Middle Panel: Slide Preview */}
          <EditMiddlePanel
            slides={slides}
            activeSlide={activeSlide}
            slidesAnimationData={slidesAnimationData}
            currentSlideBackground={currentSlideBackground}
            currentSlideBackgroundColor={currentSlideBackgroundColor}
          />

          {/* Right Panel: Properties Editor */}
          <EditRightPanel
            isReorderOnly={isReorderOnly}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            activeQuestionTab={activeQuestionTab}
            setActiveQuestionTab={setActiveQuestionTab}
            handleAddSlide={handleAddSlide}
            activeSlide={activeSlide}
            slides={slides}
            setSlides={setSlides}
            animatedSlides={animatedSlides}
            setAnimatedSlides={setAnimatedSlides}
            isCurrentSlideAnimated={isCurrentSlideAnimated}
            setIsAnimateMode={setIsAnimateMode}
            slidesAnimationData={slidesAnimationData}
            setSlidesAnimationData={setSlidesAnimationData}
            handleUpdatePresentation={handleUpdatePresentation}
            updatePresentationLoading={updatePresentationLoading}
            getLinks={getLinks}
          />
        </div>
      )}
      <Footer />
    </div>
  );
}
export default EditPresentation;
